import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { Header } from '../components/layout/Header';
import { Navigation } from '../components/layout/Navigation';
import { MobileStickyBar } from '../components/layout/MobileStickyBar';
import { Footer } from '../components/layout/Footer';
import { useScreenInit } from '../useScreenInit';
import { CategorySeo, useCategorySeo } from '../hooks/useCategorySeo';
import { usePageTitle } from '../hooks/usePageTitle';
import { CategoryData, Product } from '../data/residentialCategories';
import { ListingToolbar, ListingPagination, SortKey, PageSize, SORT_OPTIONS, PAGE_SIZE_OPTIONS } from '../components/category/ListingControls';
import { CommercialShippingCallout } from '../components/category/CommercialShippingCallout';
import { Phone, Package, ExternalLink, ChevronRight, Info } from 'lucide-react';
function parsePrice(price: string): number {
  const n = parseFloat((price || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}
function sortProducts(products: Product[], sort: SortKey): Product[] {
  const list = [...products];
  switch (sort) {
    case 'price-asc':
    case 'price-desc':
      {
        const dir = sort === 'price-asc' ? 1 : -1;
        return list.sort((a, b) => {
          const pa = parsePrice(a.price);
          const pb = parsePrice(b.price);
          const aMissing = Number.isNaN(pa);
          const bMissing = Number.isNaN(pb);
          // Priced items always sort before quote-only / missing-price items.
          if (aMissing && bMissing) return 0;
          if (aMissing) return 1;
          if (bMissing) return -1;
          return (pa - pb) * dir;
        });
      }
    case 'name-asc':
      return list.sort((a, b) => a.title.localeCompare(b.title));
    case 'name-desc':
      return list.sort((a, b) => b.title.localeCompare(a.title));
    case 'recommended':
    default:
      return list;
  }
}
function ProductCard({
  product
}: {
  product: Product;
}) {
  const [imageError, setImageError] = useState(false);
  return <div className="group bg-white rounded-xl border border-brand-border overflow-hidden hover:shadow-md hover:border-brand-maroon/50 transition-all flex flex-col h-full" data-id="element-2201" data-mp="209t27ha3hh65">
      <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block relative bg-white aspect-square p-6 border-b border-brand-border/50 flex items-center justify-center" aria-label={`View ${product.title}`} data-id="element-2202" data-mp="1vgzuckjbxspu">
        {!imageError ? <img src={product.imageUrl} alt={product.title} onError={() => setImageError(true)} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" data-id="element-2203" data-mp="1hlo0sggwu8ai" /> : <div className="w-full h-full flex flex-col items-center justify-center text-brand-charcoal/20" data-id="element-2204" data-mp="36xgayh6mefea">
            <Package size={48} strokeWidth={1} data-id="element-2205" data-mp="32ujo2ea1rx4j" />
            <span className="mt-2 text-xs font-medium tracking-wide" data-id="element-2206" data-mp="opoipih8dh5q">
              Image Unavailable
            </span>
          </div>}
      </a>

      <div className="p-5 flex flex-col flex-grow" data-id="element-2207" data-mp="tln1mqjsi6yr">
        <div className="text-xs text-gray-500 mb-1 font-mono uppercase tracking-wider" data-id="element-2208" data-mp="yfrbbhkfwt3j">
          {product.sku}
        </div>
        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-charcoal hover:text-brand-maroon transition-colors mb-3 line-clamp-2 leading-snug" data-id="element-2209" data-mp="12fi6rykwvxee">
          {product.title}
        </a>
        <div className="text-lg font-bold text-brand-charcoal mb-5 mt-auto" data-id="element-2210" data-mp="epxq5mc8dc8a">
          {product.price}
        </div>

        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block w-full bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal text-center py-2.5 rounded-md font-semibold transition-colors text-sm flex items-center justify-center gap-2" data-id="element-2211" data-mp="jr3j9l9sz1ic">
          View Details <ExternalLink size={14} data-id="element-2212" data-mp="1oqjldazhztrg" />
        </a>
      </div>
    </div>;
}
export function ResidentialCategory({
  category,
  context = 'Mailboxes',
  fallbackDescription,
  seoOverride
}: {
  category: CategoryData;
  context?: string;
  fallbackDescription?: string;
  seoOverride?: CategorySeo;
}) {
  useScreenInit();
  // Baseline title for every category page; a route-specific SEO entry (or a
  // local override) replaces it when one is available.
  usePageTitle(`${category.label} | MidAtlantic Mailboxes`);
  const seo = useCategorySeo(category.route, seoOverride);
  // Commercial branches inherit the commercial-only free-shipping callout.
  const isCommercial = context.toLowerCase().includes('commercial');
  const [searchParams, setSearchParams] = useSearchParams();
  const products = category.products;
  const hasProducts = products.length > 0;
  const sortParam = searchParams.get('sort') as SortKey | null;
  const sort: SortKey = sortParam && SORT_OPTIONS.some(o => o.value === sortParam) ? sortParam : 'recommended';
  const sizeParam = searchParams.get('pageSize');
  const pageSize: PageSize = sizeParam === 'all' ? 'all' : sizeParam && PAGE_SIZE_OPTIONS.some(o => String(o.value) === sizeParam) ? Number(sizeParam) as PageSize : 16;
  const sorted = useMemo(() => sortProducts(products, sort), [products, sort]);
  const total = sorted.length;
  const perPage = pageSize === 'all' ? total || 1 : pageSize;
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / perPage));
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(1, Number.isFinite(pageParam) ? pageParam : 1), totalPages);
  const startIdx = pageSize === 'all' ? 0 : (page - 1) * perPage;
  const visible = pageSize === 'all' ? sorted : sorted.slice(startIdx, startIdx + perPage);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = pageSize === 'all' ? total : Math.min(startIdx + perPage, total);
  const resultLabel = pageSize === 'all' ? `Showing all ${total} ${total === 1 ? 'product' : 'products'}` : `Showing ${showingFrom}-${showingTo} of ${total} products`;
  const paginationActive = pageSize !== 'all' && totalPages > 1;
  const updateParams = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => v === null ? sp.delete(k) : sp.set(k, v));
    setSearchParams(sp, {
      replace: true
    });
  };
  const onSortChange = (s: SortKey) => updateParams({
    sort: s === 'recommended' ? null : s,
    page: null
  });
  const onPageSizeChange = (p: PageSize) => updateParams({
    pageSize: p === 16 ? null : String(p),
    page: null
  });
  const onPageChange = (p: number) => updateParams({
    page: p <= 1 ? null : String(p)
  });
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-2213" data-mp="1szr2j83xrf05">
      <TopBar data-id="element-2214" data-mp="1szzdgugi0a8p" />
      <Header data-id="element-2215" data-mp="1sxd5itpp3spy" />
      <Navigation data-id="element-2216" data-mp="1x1ky51z9xstg" />

      <main className="flex-grow" data-id="element-2217" data-mp="1x12s5dmt808c">
        {/* Category Hero */}
        <section className="bg-white border-b border-brand-border py-8 lg:py-10" data-id="element-2218" data-mp="21s1bkm33heft">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2219" data-mp="21u5r61acw1vx">
            {/* Breadcrumbs */}
            <nav className="flex text-sm text-gray-500 mb-4 overflow-x-auto hide-scrollbar whitespace-nowrap pb-2" data-id="element-2220" data-mp="26qmggytakmal">
              <ol className="flex items-center space-x-2" data-id="element-2221" data-mp="2bsbp67t1knvi">
                <li data-id="element-2222" data-mp="2gqd5ikz3293a">
                  <a href="/Home.html" className="hover:text-brand-maroon transition-colors" data-id="element-2223" data-mp="2gq3gp0pry7z6">
                    Home
                  </a>
                </li>
                <li data-id="element-2224" data-mp="mdndk8s9z9yj">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2225" data-mp="qcmeliavymkp" />
                </li>
                <li data-id="element-2226" data-mp="7gnlomvrb5s1">
                  <span className="text-gray-400" data-id="element-2227" data-mp="7g463ctkwth6">{context}</span>
                </li>
                <li data-id="element-2228" data-mp="2hsr6noffss3">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2229" data-mp="2ic944e2uh64" />
                </li>
                <li className="text-brand-charcoal font-medium" data-id="element-2230" data-mp="g97cx29py82u">
                  {category.label}
                </li>
              </ol>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6" data-id="element-2231" data-mp="c9go343r620y">
              <div className="max-w-3xl" data-id="element-2232" data-mp="3kp9v8sc7215c">
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-serif font-bold text-brand-charcoal mb-4" data-id="element-2233" data-mp="3fom5e9zpbqcp">
                  {category.label}
                </h1>
                {seo?.description || fallbackDescription ? <p className="text-lg text-brand-muted leading-relaxed" data-id="element-2234" data-mp="3onzjxh2on54a">
                    {seo?.description || fallbackDescription}
                  </p> : <div className="space-y-2.5 max-w-2xl" aria-hidden="true" role="presentation" data-id="element-2235" data-mp="13zbivgadqjx0">
                    <div className="h-4 rounded bg-brand-border/60 w-full" data-id="element-2236" data-mp="1ggw2hld3ev4t" />
                    <div className="h-4 rounded bg-brand-border/60 w-11/12" data-id="element-2237" data-mp="1get1k6aejhlb" />
                    <div className="h-4 rounded bg-brand-border/60 w-3/5" data-id="element-2238" data-mp="12j7cy082yztf" />
                  </div>}
              </div>

              <div className="shrink-0 bg-brand-goldWash border border-brand-gold/30 rounded-xl p-5 flex items-center gap-4" data-id="element-2239" data-mp="1v6a9wnx3z2hk">
                <div className="w-10 h-10 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm shrink-0" data-id="element-2240" data-mp="205eltmbf3ebl">
                  <Phone size={20} data-id="element-2241" data-mp="1lfyvu8jpncwy" />
                </div>
                <div data-id="element-2242" data-mp="1qejui3jbdoja">
                  <p className="text-sm font-bold text-brand-charcoal mb-0.5" data-id="element-2243" data-mp="1qdqx3t68wbdt">
                    Need help choosing?
                  </p>
                  <a href="tel:301-620-7585" className="text-brand-maroon font-semibold hover:underline" data-id="element-2244" data-mp="2ivmfzex52ufn">
                    Call 301-620-7585
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {isCommercial && <CommercialShippingCallout data-id="element-2245" data-mp="17qjdo0o82ea8" />}

        {/* Product Grid */}
        <section className="py-8 lg:py-10" data-id="element-2246" data-mp="1cota0elonkxf">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2247" data-mp="1cni86kbssn8y">
            {hasProducts ? <>
                {/* Listing controls toolbar */}
                <div className="mb-6" data-id="element-2248" data-mp="12qna22jfsov7">
                  <ListingToolbar sort={sort} onSortChange={onSortChange} pageSize={pageSize} onPageSizeChange={onPageSizeChange} resultLabel={resultLabel} data-id="element-2249" data-mp="12roxz7ljx5uc" />
                </div>

                {paginationActive && <div className="mb-6" data-id="element-2250" data-mp="f35qguabsx3b">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2251" data-mp="k0p35prdl1t5" />
                  </div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-2252" data-mp="3rlym1pty8z76">
                  {visible.map((product, idx) => <ProductCard key={`${product.sku}-${startIdx}-${idx}`} product={product} data-id="element-2253" data-mp="5dqe21fzqjhl" data-mpi={idx} />)}
                </div>

                {paginationActive && <div className="mt-8" data-id="element-2254" data-mp="eddeajwzhj92">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2255" data-mp="t31j3v4dj01j" />
                  </div>}
              </> : <div className="bg-white border border-brand-border rounded-xl p-8 lg:p-12 text-center max-w-2xl mx-auto" data-id="element-2256" data-mp="12498fnowwqjh">
                <div className="w-14 h-14 rounded-full bg-brand-goldWash border border-brand-gold/30 text-brand-maroon flex items-center justify-center mx-auto mb-5" data-id="element-2257" data-mp="y0b510sk0tkm">
                  <Package size={26} strokeWidth={1.5} data-id="element-2258" data-mp="1a4xa1m1jhodh" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-2" data-id="element-2259" data-mp="1f38mm4j7ycze">
                  Browse {category.label} with our team
                </h2>
                <p className="text-brand-muted leading-relaxed mb-6" data-id="element-2260" data-mp="1nvtxkyglzfdo">
                  We carry a full selection in this category. Tell us what
                  you’re looking for and we’ll help you find the right option.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2261" data-mp="1xvb57lff83ca">
                  <a href="tel:301-620-7585" className="inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroonDark text-white px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2262" data-mp="22tcnw58xq0n8">
                    <Phone size={16} data-id="element-2263" data-mp="2lpbj57bjdtj2" /> Call 301-620-7585
                  </a>
                  <a href="/ContactUs.html" className="inline-flex items-center justify-center gap-2 bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2264" data-mp="2lrfrc1ggxep5">
                    Ask for help
                  </a>
                </div>
              </div>}
          </div>
        </section>

        {/* Buyer guidance / SEO supporting copy */}
        {seo?.extraText && <section className="pb-12 lg:pb-16" data-id="element-2265" data-mp="2vmg26ya358vn">
            <div className="max-w-7xl mx-auto px-4" data-id="element-2266" data-mp="2vpc4szwvolqf">
              <div className="bg-white border border-brand-border rounded-xl p-6 lg:p-8 flex flex-col sm:flex-row gap-5" data-id="element-2267" data-mp="2qnmph82mhlwj">
                <div className="hidden sm:flex w-11 h-11 rounded-xl bg-brand-goldWash border border-brand-gold/30 text-brand-maroon items-center justify-center shrink-0" data-id="element-2268" data-mp="1k9nh26bsviok">
                  <Info size={22} strokeWidth={1.75} data-id="element-2269" data-mp="334w4ihj0azvp" />
                </div>
                <div className="max-w-3xl" data-id="element-2270" data-mp="37x793xooz9ul">
                  <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-3" data-id="element-2271" data-mp="37xyqa3hjl51c">
                    About {category.label}
                  </h2>
                  <p className="text-brand-muted leading-relaxed whitespace-pre-line" data-id="element-2272" data-mp="2z3kewc04ueni">
                    {seo.extraText}
                  </p>
                </div>
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-2273" data-mp="3quqgkwvi4kew" />
      <MobileStickyBar data-id="element-2274" data-mp="3qt731e18cngt" />
    </div>;
}